import React, { useState, useEffect } from 'react';
import { 
  Input, 
  Modal, 
  Form, 
  Select, 
  App,
  Spin,
  Dropdown,
  Empty
} from 'antd';
import { 
  Plus, 
  Search,
  MoreHorizontal,
  Edit3,
  Trash2,
  Key,
  UserX,
  UserCheck,
  User,
  Mail,
  Shield,
  Eye,
  Settings,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  BackendUser,
  UserRole,
  getRoleName,
  CreateUserData,
  UpdateUserData,
  ChangePasswordData
} from '@/types/user';
import { userService } from '@/api/users';

// Role configuration with icons and colors
const roleConfig: Record<UserRole, { icon: React.ReactNode; bgColor: string; textColor: string }> = {
  admin: { 
    icon: <Shield className="w-3.5 h-3.5" />, 
    bgColor: 'bg-blue-50', 
    textColor: 'text-dark-blue' 
  },
  operator: { 
    icon: <Settings className="w-3.5 h-3.5" />, 
    bgColor: 'bg-green/10', 
    textColor: 'text-green' 
  },
  viewer: { 
    icon: <Eye className="w-3.5 h-3.5" />, 
    bgColor: 'bg-gray-100', 
    textColor: 'text-dark-grey' 
  }
};

export const UserManagement: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  
  // Add styles to disable dropdown animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .user-action-dropdown {
        animation: none !important;
      }
      .user-action-dropdown .ant-dropdown-menu {
        animation: none !important;
        transition: none !important;
      }
      .select-dropdown-14 {
        font-size: 14px !important;
      }
      .select-dropdown-14 .ant-select-item-option-content {
        font-size: 14px !important;
      }
      .select-placeholder-14 .ant-select-selection-placeholder {
        font-size: 14px !important;
      }
      .select-placeholder-14 .ant-select-selection-item {
        font-size: 14px !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // State
  const [users, setUsers] = useState<BackendUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  
  // Selected user for operations
  const [selectedUser, setSelectedUser] = useState<BackendUser | null>(null);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers();
      setUsers(data);
    } catch (error) {
      message.error('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get user role - handle both primary_site_role and role fields
  const getUserRole = (user: BackendUser): UserRole => {
    const role = user.primary_site_role || user.role || 'viewer';
    // Map any backend role to our 3 simplified roles
    if (role === 'admin' || role === 'superuser' || role === 'system_admin') return 'admin';
    if (role === 'operator' || role === 'manager' || role === 'facilities_manager' || role === 'building_engineer') return 'operator';
    return 'viewer';
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Group users by role
  const groupedUsers = filteredUsers.reduce((acc, user) => {
    const role = getUserRole(user);
    if (!acc[role]) acc[role] = [];
    acc[role].push(user);
    return acc;
  }, {} as Record<UserRole, BackendUser[]>);

  // Handlers
  const handleCreate = () => {
    form.resetFields();
    setIsCreateModalOpen(true);
  };

  const handleEdit = (user: BackendUser) => {
    setSelectedUser(user);
    const role = getUserRole(user);
    form.setFieldsValue({
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      role: role,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (user: BackendUser) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleChangePassword = (user: BackendUser) => {
    setSelectedUser(user);
    passwordForm.resetFields();
    setIsPasswordModalOpen(true);
  };

  const handleToggleStatus = (user: BackendUser) => {
    setSelectedUser(user);
    setIsStatusModalOpen(true);
  };

  // API Operations
  const handleCreateSubmit = async (values: any) => {
    try {
      const createData: CreateUserData = {
        username: values.email, // Use email as username
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        password: values.password,
        role: values.role,
      };
      
      await userService.createUser(createData);
      message.success('User created successfully');
      setIsCreateModalOpen(false);
      loadUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to create user');
      console.error('Error creating user:', error);
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!selectedUser) return;
    
    try {
      const updateData: UpdateUserData = {
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        role: values.role,
      };
      
      await userService.updateUser(selectedUser.id, updateData);
      message.success('User updated successfully');
      setIsEditModalOpen(false);
      loadUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user');
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    
    try {
      await userService.deleteUser(selectedUser.id);
      message.success('User deleted successfully');
      setIsDeleteModalOpen(false);
      loadUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete user');
      console.error('Error deleting user:', error);
    }
  };

  const handlePasswordSubmit = async (values: any) => {
    if (!selectedUser) return;
    
    try {
      const passwordData: ChangePasswordData = {
        password: values.newPassword,
        old_password: values.oldPassword,
      };
      
      await userService.changePassword(selectedUser.id, passwordData);
      message.success('Password changed successfully');
      setIsPasswordModalOpen(false);
    } catch (error: any) {
      message.error(error.message || 'Failed to change password');
      console.error('Error changing password:', error);
    }
  };

  const handleStatusConfirm = async () => {
    if (!selectedUser) return;
    
    try {
      const isActive = selectedUser.is_active !== false;
      if (isActive) {
        await userService.deactivateUser(selectedUser.id);
        message.success('User deactivated successfully');
      } else {
        await userService.activateUser(selectedUser.id);
        message.success('User activated successfully');
      }
      setIsStatusModalOpen(false);
      loadUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user status');
      console.error('Error updating status:', error);
    }
  };

  const getActionMenuItems = (user: BackendUser) => ({
    items: [
      {
        key: 'edit',
        icon: <Edit3 className="w-4 h-4" />,
        label: <span className="paragraph-12">Edit</span>,
        onClick: () => handleEdit(user),
      },
      {
        key: 'password',
        icon: <Key className="w-4 h-4" />,
        label: <span className="paragraph-12">Change Password</span>,
        onClick: () => handleChangePassword(user),
      },
      {
        key: 'status',
        icon: user.is_active !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />,
        label: <span className="paragraph-12">{user.is_active !== false ? 'Deactivate' : 'Activate'}</span>,
        onClick: () => handleToggleStatus(user),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        icon: <Trash2 className="w-4 h-4" />,
        label: <span className="paragraph-12">Delete</span>,
        danger: true,
        onClick: () => handleDelete(user),
      },
    ],
  });

  // Helper function to get user initials
  const getUserInitials = (user: BackendUser): string => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    } else if (user.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const UserCard = ({ user }: { user: BackendUser }) => {
    const role = getUserRole(user);
    const isActive = user.is_active !== false;
    const config = roleConfig[role];
    const initials = getUserInitials(user);
    const displayName = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}`
      : user.username;

    return (
      <div className="group relative bg-white rounded-lg p-4 border border-light-grey hover:border-primary/20 hover:shadow-sm transition-all duration-200">
        <div className="flex items-center justify-between">
          {/* Left side - User info */}
          <div className="flex items-center gap-3 flex-1">
            {/* Avatar with initials */}
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm",
              config.bgColor,
              config.textColor
            )}>
              {initials}
            </div>
            
            {/* User details - all on same line */}
            <div className="flex items-center gap-4 flex-1">
              <h4 className="heading-10 text-black-blue">
                {displayName}
              </h4>
              
              <span className="paragraph-12 text-dark-grey flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {user.email || user.username}
              </span>
              
              {user.last_login && (
                <span className="paragraph-11 text-dark-grey">
                  Last login: {new Date(user.last_login).toLocaleDateString()}
                </span>
              )}
              
              <div className="flex items-center gap-2 ml-auto mr-4">
                {/* Role badge */}
                <div className={cn(
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-md",
                  config.bgColor
                )}>
                  {config.icon}
                  <span className={cn("paragraph-11 font-medium", config.textColor)}>
                    {getRoleName(role)}
                  </span>
                </div>
                
                {/* Status badge */}
                <div className={cn(
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-md paragraph-11",
                  isActive 
                    ? "bg-green/10 text-green" 
                    : "bg-danger/10 text-danger"
                )}>
                  <div className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    isActive ? "bg-green" : "bg-danger"
                  )} />
                  {isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>
          </div>

          {/* Actions dropdown */}
          <Dropdown
            menu={getActionMenuItems(user)}
            trigger={['click']}
            placement="bottomRight"
            transitionName=""
            overlayClassName="user-action-dropdown"
          >
            <button className="p-1.5 hover:bg-light-grey rounded-lg transition-all">
              <MoreHorizontal className="w-4 h-4 text-dark-grey" />
            </button>
          </Dropdown>
        </div>
      </div>
    );
  };

  const UserCardSkeleton = () => {
    return (
      <div className="bg-white rounded-lg p-4 border border-light-grey">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {/* Avatar skeleton */}
            <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse" />
            
            {/* User details skeleton */}
            <div className="flex items-center gap-4 flex-1">
              <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
              
              <div className="flex items-center gap-2 ml-auto mr-4">
                <div className="h-6 w-20 bg-gray-200 rounded-md animate-pulse" />
                <div className="h-6 w-16 bg-gray-200 rounded-md animate-pulse" />
              </div>
            </div>
          </div>
          
          {/* Actions skeleton */}
          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-6 text-dark-blue">User Management</h1>
        </div>
        <Button
          variant="outline"
          size="md"
          className="border-primary text-primary hover:bg-primary hover:text-white transition-all"
          onClick={handleCreate}
        >
          <Plus className="w-3.5 h-3.5" />
          <span className="button-s ml-1">Add User</span>
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative w-full max-w-sm">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 h-9 w-full border-light-grey/50 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 paragraph-11 placeholder:text-dark-grey/50 transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-dark-grey hover:text-black-blue transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* User List */}
      {loading ? (
        <div className="space-y-6">
          {/* Admin skeleton */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="grid gap-2">
              <UserCardSkeleton />
              <UserCardSkeleton />
            </div>
          </div>
          
          {/* Operator skeleton */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="grid gap-2">
              <UserCardSkeleton />
              <UserCardSkeleton />
              <UserCardSkeleton />
            </div>
          </div>
          
          {/* Viewer skeleton */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="grid gap-2">
              <UserCardSkeleton />
            </div>
          </div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="paragraph-11 text-dark-grey">
              {searchTerm ? 'No users found matching your search' : 'No users found'}
            </span>
          }
        />
      ) : (
        <div className="space-y-6">
          {(['admin', 'operator', 'viewer'] as UserRole[]).map(role => {
            const roleUsers = groupedUsers[role] || [];
            if (roleUsers.length === 0) return null;

            return (
              <div key={role}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="heading-7 text-black-blue">
                    {getRoleName(role)}s
                  </h3>
                  <span className="paragraph-11 text-dark-grey">
                    ({roleUsers.length})
                  </span>
                </div>
                <div className="grid gap-2">
                  {roleUsers.map(user => (
                    <UserCard key={user.id} user={user} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create User Modal */}
      <Modal
        title={<h3 className="heading-6 text-black-blue">Create New User</h3>}
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        footer={null}
        width={600}
        centered
        transitionName=""
        maskTransitionName=""
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
          className="mt-6"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label={<span className="paragraph-14 text-dark-grey">First Name</span>}
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input 
                placeholder="John" 
                className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
              />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={<span className="paragraph-14 text-dark-grey">Last Name</span>}
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input 
                placeholder="Doe"
                className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label={<span className="paragraph-14 text-dark-grey">Email Address</span>}
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input 
              placeholder="john.doe@company.com"
              className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
            />
          </Form.Item>

          <Form.Item
            name="role"
            label={<span className="paragraph-14 text-dark-grey">Role</span>}
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select 
              placeholder="Select a role"
              className="h-10 select-placeholder-14"
              dropdownClassName="select-dropdown-14"
            >
              <Select.Option value="admin">
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-dark-blue" />
                  <span className="paragraph-14">Administrator</span>
                </div>
              </Select.Option>
              <Select.Option value="operator">
                <div className="flex items-center gap-2">
                  <Settings className="w-3.5 h-3.5 text-green" />
                  <span className="paragraph-14">Operator</span>
                </div>
              </Select.Option>
              <Select.Option value="viewer">
                <div className="flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5 text-dark-grey" />
                  <span className="paragraph-14">Viewer</span>
                </div>
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="password"
            label={<span className="paragraph-14 text-dark-grey">Password</span>}
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password 
              placeholder="••••••••"
              className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
            />
          </Form.Item>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-light-grey">
            <Button
              variant="ghost"
              size="md"
              onClick={() => setIsCreateModalOpen(false)}
            >
              <span className="button-s">Cancel</span>
            </Button>
            <Button
              variant="default"
              size="md"
              htmlType="submit"
              className="bg-primary text-white hover:bg-primary/90"
            >
              <span className="button-s">Create User</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={<h3 className="heading-6 text-black-blue">Edit User</h3>}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        width={600}
        centered
        transitionName=""
        maskTransitionName=""
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
          className="mt-6"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label={<span className="paragraph-14 text-dark-grey">First Name</span>}
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input 
                placeholder="John"
                className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
              />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={<span className="paragraph-14 text-dark-grey">Last Name</span>}
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input 
                placeholder="Doe"
                className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label={<span className="paragraph-14 text-dark-grey">Email Address</span>}
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input 
              placeholder="john.doe@company.com"
              className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
            />
          </Form.Item>

          <Form.Item
            name="role"
            label={<span className="paragraph-14 text-dark-grey">Role</span>}
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select 
              placeholder="Select a role"
              className="h-10 select-placeholder-14"
              dropdownClassName="select-dropdown-14"
            >
              <Select.Option value="admin">
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-dark-blue" />
                  <span className="paragraph-14">Administrator</span>
                </div>
              </Select.Option>
              <Select.Option value="operator">
                <div className="flex items-center gap-2">
                  <Settings className="w-3.5 h-3.5 text-green" />
                  <span className="paragraph-14">Operator</span>
                </div>
              </Select.Option>
              <Select.Option value="viewer">
                <div className="flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5 text-dark-grey" />
                  <span className="paragraph-14">Viewer</span>
                </div>
              </Select.Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-light-grey">
            <Button
              variant="ghost"
              size="md"
              onClick={() => setIsEditModalOpen(false)}
            >
              <span className="button-s">Cancel</span>
            </Button>
            <Button
              variant="default"
              size="md"
              htmlType="submit"
              className="bg-primary text-white hover:bg-primary/90"
            >
              <span className="button-s">Save Changes</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title={<h3 className="heading-6 text-black-blue">Change Password</h3>}
        open={isPasswordModalOpen}
        onCancel={() => setIsPasswordModalOpen(false)}
        footer={null}
        width={480}
        centered
        transitionName=""
        maskTransitionName=""
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
          className="mt-6"
        >
          <Form.Item
            name="oldPassword"
            label={<span className="paragraph-14 text-dark-grey">Current Password</span>}
            rules={[{ required: true, message: 'Please enter current password' }]}
          >
            <Input.Password 
              placeholder="••••••••"
              className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={<span className="paragraph-14 text-dark-grey">New Password</span>}
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password 
              placeholder="••••••••"
              className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={<span className="paragraph-14 text-dark-grey">Confirm Password</span>}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password 
              placeholder="••••••••"
              className="h-10 border-light-grey rounded-lg focus:border-primary paragraph-14"
            />
          </Form.Item>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-light-grey">
            <Button
              variant="ghost"
              size="md"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              <span className="button-s">Cancel</span>
            </Button>
            <Button
              variant="default"
              size="md"
              htmlType="submit"
              className="bg-primary text-white hover:bg-primary/90"
            >
              <span className="button-s">Change Password</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        footer={null}
        width={420}
        centered
        transitionName=""
        maskTransitionName=""
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-danger/10 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div className="flex-1">
            <h3 className="heading-6 text-black-blue mb-2">Delete User</h3>
            <p className="paragraph-11 text-dark-grey mb-6">
              Are you sure you want to delete <strong className="text-black-blue">
                {selectedUser?.first_name && selectedUser?.last_name 
                  ? `${selectedUser.first_name} ${selectedUser.last_name}`
                  : selectedUser?.username}
              </strong>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                size="md"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                <span className="button-s">Cancel</span>
              </Button>
              <Button
                variant="default"
                size="md"
                onClick={handleDeleteConfirm}
                className="bg-danger text-white hover:bg-danger/90"
              >
                <span className="button-s">Delete User</span>
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Status Change Confirmation */}
      <Modal
        open={isStatusModalOpen}
        onCancel={() => setIsStatusModalOpen(false)}
        footer={null}
        width={420}
        centered
        transitionName=""
        maskTransitionName=""
      >
        <div className="flex items-start gap-4">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
            selectedUser?.is_active !== false 
              ? "bg-warning/10" 
              : "bg-green/10"
          )}>
            {selectedUser?.is_active !== false ? (
              <UserX className="w-5 h-5 text-warning" />
            ) : (
              <UserCheck className="w-5 h-5 text-green" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="heading-6 text-black-blue mb-2">
              {selectedUser?.is_active !== false ? 'Deactivate' : 'Activate'} User
            </h3>
            <p className="paragraph-11 text-dark-grey mb-6">
              Are you sure you want to {selectedUser?.is_active !== false ? 'deactivate' : 'activate'}{' '}
              <strong className="text-black-blue">
                {selectedUser?.first_name && selectedUser?.last_name 
                  ? `${selectedUser.first_name} ${selectedUser.last_name}`
                  : selectedUser?.username}
              </strong>?
              {selectedUser?.is_active !== false && (
                <span className="block mt-2 text-danger">
                  This user will no longer be able to access the system.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                size="md"
                onClick={() => setIsStatusModalOpen(false)}
              >
                <span className="button-s">Cancel</span>
              </Button>
              <Button
                variant="default"
                size="md"
                onClick={handleStatusConfirm}
                className={cn(
                  "text-white",
                  selectedUser?.is_active !== false 
                    ? "bg-warning hover:bg-warning/90" 
                    : "bg-green hover:bg-green/90"
                )}
              >
                <span className="button-s">
                  {selectedUser?.is_active !== false ? 'Deactivate' : 'Activate'}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};